import React, { useState, useEffect, useCallback, useMemo } from 'react'
import { Link, useNavigate, useOutletContext } from 'react-router-dom'
import { canRoundAcceptBet, lotteryService } from '../../services/lotteryService'
import type { LotteryRound } from '../../services/lotteryService'
import type { LobbyHuayOutletContext } from './ModernLobbyHuayLayout'
import './ModernLobbyHuayYeekeePage.css'

/** หน้ายี่กี — UI ใหม่ อ่านง่าย (ลำดับ: รอบ → สถานะ → เวลา → ปิดรับ) */
const ModernLobbyHuayYeekeePage: React.FC = () => {
  const { themeConfig } = useOutletContext<LobbyHuayOutletContext>()
  const navigate = useNavigate()
  const wipWupMinutes = themeConfig?.settings?.wipWupMinutes ?? 15
  const [rounds, setRounds] = useState<LotteryRound[]>([])
  const [loading, setLoading] = useState(true)
  const [countdown, setCountdown] = useState<Record<string, string>>({})

  const loadRounds = useCallback(async (cacheBust = false) => {
    try {
      setLoading(true)
      const todayRounds = await lotteryService.getTodayRounds(cacheBust)
      const yeekeeRounds = todayRounds.filter(
        (round) =>
          round.huayTypeName?.includes('ยี') ||
          round.huayName?.includes('ยี')
      )
      setRounds(yeekeeRounds)
    } catch (error) {
      console.error('Error loading Yeekee rounds:', error)
      setRounds([])
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    loadRounds()
    const id = setInterval(loadRounds, 30_000)
    return () => clearInterval(id)
  }, [loadRounds])

  useEffect(() => {
    const handleRoundUpdated = () => loadRounds(true)
    window.addEventListener('lottery_round_updated', handleRoundUpdated)
    return () => window.removeEventListener('lottery_round_updated', handleRoundUpdated)
  }, [loadRounds])

  useEffect(() => {
    if (rounds.length === 0) return
    const tick = () => {
      const next: Record<string, string> = {}
      rounds.forEach((round) => {
        next[round._id] = lotteryService.formatTimeRemaining(round.betEndTime)
      })
      setCountdown(next)
    }
    tick()
    const id = setInterval(tick, 1000)
    return () => clearInterval(id)
  }, [rounds])

  const sortedRounds = useMemo(() => {
    if (rounds.length === 0) return []
    const sortedByNumber = [...rounds].sort((a, b) => {
      const aNum = parseInt(a.roundNumber.split('-')[2] || '0', 10)
      const bNum = parseInt(b.roundNumber.split('-')[2] || '0', 10)
      return aNum - bNum
    })
    const now = new Date()
    const openRounds = sortedByNumber
      .filter((r) => canRoundAcceptBet(r))
      .map((r) => ({ ...r, diff: new Date(r.betEndTime).getTime() - now.getTime() }))
      .filter((r) => r.diff > 0)
    if (openRounds.length === 0) return sortedByNumber
    const currentRound = openRounds.sort((a, b) => a.diff - b.diff)[0]
    const currentRoundNum = parseInt(currentRound.roundNumber.split('-')[2] || '0', 10)
    const fromCurrent = sortedByNumber.filter(
      (r) => parseInt(r.roundNumber.split('-')[2] || '0', 10) >= currentRoundNum
    )
    const beforeCurrent = sortedByNumber.filter(
      (r) => parseInt(r.roundNumber.split('-')[2] || '0', 10) < currentRoundNum
    )
    return [...fromCurrent, ...beforeCurrent]
  }, [rounds])

  const openCount = useMemo(
    () => sortedRounds.filter((r) => canRoundAcceptBet(r)).length,
    [sortedRounds]
  )

  const handleRoundClick = (round: LotteryRound, isOpen: boolean) => {
    if (isOpen) {
      navigate('/lobby-huay/lottery-terms', {
        state: { round, lotteryType: 'yeekee' as const },
      })
      return
    }
    navigate(`/lobby-huay/yeekee/result?roundId=${round._id}`, { state: { round } })
  }

  return (
    <div className="htd-yeekee">
      <div className="htd-yeekee__top">
        <Link to="/lobby-huay/listall" className="htd-yeekee__back">
          <i className="fas fa-chevron-left" aria-hidden />
          กลับหน้าหลัก
        </Link>

        <div className="htd-yeekee__heading">
          <h1 className="htd-yeekee__title">จับยี่กี</h1>
          <div className="htd-yeekee__summary">
            <span className="htd-yeekee__pill">{rounds.length} รอบ</span>
            <span className="htd-yeekee__pill htd-yeekee__pill--open">{openCount} เปิดรับ</span>
          </div>
        </div>
        <p className="htd-yeekee__hint">กดการ์ดรอบที่เปิดรับเพื่อเข้าแทง · รอบที่ปิดแล้วกดดูผล</p>
      </div>

      {loading ? (
        <div className="htd-yeekee__state">
          <i className="fas fa-spinner fa-spin" aria-hidden />
          <p className="mt-2 mb-0">กำลังโหลดรอบยี่กี...</p>
        </div>
      ) : sortedRounds.length === 0 ? (
        <div className="htd-yeekee__state">
          <p className="mb-0">ไม่มีรอบยี่กีในวันนี้</p>
        </div>
      ) : (
        <div className="htd-yeekee__grid" role="list">
          {sortedRounds.map((round, index) => {
            const isOpen = canRoundAcceptBet(round)
            const betEndDate = new Date(round.betEndTime)
            const closeTimeShort = betEndDate.toLocaleTimeString('th-TH', {
              timeZone: 'Asia/Bangkok',
              hour: '2-digit',
              minute: '2-digit',
            })
            const roundNum = round.roundNumber.split('-')[2] || String(index + 1)
            const countdownStr =
              countdown[round._id] ?? lotteryService.formatTimeRemaining(round.betEndTime)
            const minutesRemaining = (betEndDate.getTime() - Date.now()) / (1000 * 60)
            const isNear = isOpen && minutesRemaining <= wipWupMinutes && minutesRemaining > 0

            return (
              <button
                key={round._id}
                type="button"
                role="listitem"
                className={[
                  'htd-yeekee__card',
                  isOpen ? 'is-open' : 'is-closed',
                  isNear ? 'is-near' : '',
                ]
                  .filter(Boolean)
                  .join(' ')}
                style={{ animationDelay: `${Math.min(index, 18) * 0.03}s` }}
                onClick={() => handleRoundClick(round, isOpen)}
                aria-label={`${isOpen ? 'แทง' : 'ดูผล'} รอบที่ ${roundNum}`}
              >
                <div className="htd-yeekee__row">
                  <h2 className="htd-yeekee__round">รอบที่ {roundNum}</h2>
                  <span
                    className={`htd-yeekee__badge ${isOpen ? 'htd-yeekee__badge--open' : 'htd-yeekee__badge--closed'}`}
                  >
                    {isOpen ? 'เปิดรับ' : 'ปิดแล้ว'}
                  </span>
                </div>

                <p className={`htd-yeekee__time${isNear ? ' wip_wup' : ''}`}>
                  {isOpen ? countdownStr : 'ปิดรับแทง'}
                </p>

                <p className="htd-yeekee__close">ปิดรับ {closeTimeShort} น.</p>
                <p className="htd-yeekee__cta">{isOpen ? 'แตะเพื่อแทง →' : 'แตะเพื่อดูผล →'}</p>
              </button>
            )
          })}
        </div>
      )}
    </div>
  )
}

export default ModernLobbyHuayYeekeePage
